import { Injectable } from '@angular/core';
import { IqCssClassnames, IqCssClassNamesRule } from '../../models';
import {
    DatiqRulesConditionOperators,
    IDatiqRulesCondition,
    IDatiqRulesData,
    IDatiqRulesRule,
} from '../../models/iq-rules.models';
import { isEqual, uniq, uniqWith } from 'lodash';

type DashboardCacheNewRule = {
    type: 'dashboard:cache';
    dashboardId: string;
    path: '';
    rule: {
        operator: 'contains';
        value: Record<string, string | boolean | number>;
    };
};

@Injectable({
    providedIn: 'root',
})
export class IqStyleProcessorMigratorService {
    constructor() {}

    mapToNewCssRules(cssClassNames: IqCssClassnames): IqCssClassNamesRule[] {
        const legacyRuleGroups: {
            classNames: string[];
            cssSelectors: string[];
            rules: IDatiqRulesRule[];
        }[] = this.mapToLegacyRuleGroups(cssClassNames);

        return legacyRuleGroups.map((legacyRuleGroup): IqCssClassNamesRule => {
            return {
                classNames: legacyRuleGroup.classNames,
                rules: legacyRuleGroup?.rules?.length
                    ? legacyRuleGroup.rules
                          .map(
                              (
                                  rule,
                              ): { [key: string]: any; type: string }[] => {
                                  return rule.conditions.map(
                                      (
                                          condition: IDatiqRulesCondition,
                                      ): {
                                          [key: string]: any;
                                          type: string;
                                      } => {
                                          return this.mapConditionToNewRule(
                                              condition,
                                          );
                                      },
                                  );
                              },
                          )
                          .flat()
                    : [],
                cssSelectors: legacyRuleGroup.cssSelectors,
            };
        });
    }
    mapConditionToNewRule(condition: IDatiqRulesCondition): {
        [key: string]: any;
        type: string;
    } {
        const newRule = {
            DashboardCache: (
                condition: IDatiqRulesCondition,
            ): DashboardCacheNewRule => this.mapDashboardCacheRule(condition),
        }[condition.type as string]?.(condition);

        if (!newRule) console.warn('No rule found for condition', condition);

        return (
            newRule ?? {
                type: 'condition-rule:unknown:style-processor-cssclasses',
            }
        );
    }

    mapDashboardCacheRule(
        condition: IDatiqRulesCondition,
    ): DashboardCacheNewRule {
        const value: { id: string; data: Record<string, any> } =
            condition.value as { id: string; data: Record<string, any> };

        return {
            type: 'dashboard:cache',
            dashboardId: value.id,
            path: '',
            rule: {
                operator: 'contains',
                value: this.flattenKeys(value.data),
            },
        };
    }

    mapToLegacyRuleGroups(cssClassNames: IqCssClassnames): {
        classNames: string[];
        cssSelectors: string[];
        rules: IDatiqRulesRule[];
    }[] {
        const uniqueRules: IDatiqRulesRule[][] =
            this.getUniqueRules(cssClassNames);

        return uniqueRules
            .map((rules: IDatiqRulesRule[]) => {
                const cssSelectors: {
                    cssSelectors: string[];
                    rules: IDatiqRulesRule[];
                }[] = this.getCssSelectors(rules, cssClassNames);

                return cssSelectors.map(
                    (cssSelector: {
                        cssSelectors: string[];
                        rules: IDatiqRulesRule[];
                    }): {
                        classNames: string[];
                        cssSelectors: string[];
                        rules: IDatiqRulesRule[];
                    } => {
                        const classNames: string[] = this.getClassNames(
                            cssSelector,
                            cssClassNames,
                        );

                        return {
                            classNames,
                            cssSelectors: cssSelector.cssSelectors,
                            rules: cssSelector.rules,
                        };
                    },
                );
            })
            .flat();
    }
    getClassNames(
        cssSelector: {
            cssSelectors: string[];
            rules: IDatiqRulesRule[];
        },
        cssClassNames: IqCssClassnames,
    ): string[] {
        return Object.keys(cssClassNames).filter((key) => {
            return cssClassNames[key].some((ruleData) => {
                return isEqual(
                    {
                        rules: cssSelector.rules,
                        data: cssSelector.cssSelectors,
                    },
                    {
                        rules: ruleData.rules,
                        data: ruleData.data,
                    },
                );
            });
        });
    }
    getCssSelectors(
        rules: IDatiqRulesRule[],
        cssClassNames: IqCssClassnames,
    ): { cssSelectors: string[]; rules: IDatiqRulesRule[] }[] {
        return Object.values(cssClassNames)
            .map(
                (
                    ruleDataArray: IDatiqRulesData<string[]>[],
                ): { cssSelectors: string[]; rules: IDatiqRulesRule[] }[] => {
                    const selectorArrays: string[][] = ruleDataArray
                        .filter((ruleData: IDatiqRulesData<string[]>) => {
                            return isEqual(ruleData.rules, rules);
                        })
                        .map((ruleData) => ruleData.data);

                    return uniq(selectorArrays).map(
                        (
                            selectors,
                        ): {
                            cssSelectors: string[];
                            rules: IDatiqRulesRule[];
                        } => {
                            return {
                                cssSelectors: selectors,
                                rules,
                            };
                        },
                    );
                },
            )
            .flat();
    }
    getUniqueRules(cssClassNames: IqCssClassnames): IDatiqRulesRule[][] {
        const ruleDataList: IDatiqRulesData<string[]>[][] =
            Object.values(cssClassNames);
        const rules: IDatiqRulesRule[][] = ruleDataList.reduce(
            (
                allRules: IDatiqRulesRule[][],
                ruleDataArray: IDatiqRulesData<string[]>[],
            ): IDatiqRulesRule[][] => {
                return [
                    ...allRules,
                    ...ruleDataArray.map((ruleData) => {
                        return ruleData.rules;
                    }),
                ];
            },
            [],
        );

        return uniqWith(rules, isEqual);
    }

    flattenKeys(object, initialPathPrefix = '') {
        if (!object || typeof object !== 'object') {
            return [{ [initialPathPrefix]: object }];
        }

        const prefix = initialPathPrefix
            ? Array.isArray(object)
                ? initialPathPrefix
                : `${initialPathPrefix}.`
            : '';

        return Object.keys(object)
            .flatMap((key) =>
                this.flattenKeys(
                    object[key],
                    Array.isArray(object)
                        ? `${prefix}[${key}]`
                        : `${prefix}${key}`,
                ),
            )
            .reduce((acc, path) => ({ ...acc, ...path }));
    }
}
